import { MouseEvent } from 'react';
import { Info } from 'lucide-react';
import { DateTime } from 'luxon';

import styles from './about.module.css';

import config from '@/config';

import {
	Dialog,
	DialogContent,
	dialogCardBackdropClassName,
	dialogCardClassName,
} from '@shared/ui/dialog';
import { cn } from '@/lib/utils';
import { useDialog } from '@shared/lib/hooks';
import { exportDebugZip } from '@shared/lib/debug-log';
import { useClearAppData } from '@features/clear-app-data';
import { Logger } from '@shared/lib/logger';
import { sanitizeHtml } from '@/lib/article-renderer/core/sanitize-html';
import { T, useT } from '@shared/i18n';

const logger = new Logger('about');

export const About = ({ show, setShow }: AboutProps) => {
	const t = useT();
	const { title, version, about, buildNumber } = config.build;
	// buildNumber is a Unix epoch (seconds) timestamp -- see
	// scripts/prepare.js's getBuildId().
	const buildDate = DateTime.fromSeconds(buildNumber).toLocaleString(
		DateTime.DATETIME_FULL,
	);
	const handleClick = (e: MouseEvent<HTMLSpanElement>) => {
		switch (e.detail) {
			case 2:
				window.location.replace('/drafts');
				break;
		}
	};

	// "Clear everything except auth" -- a reader hitting this expects a true
	// reset of whatever's gone stale (a bad cached response, a stuck offline
	// download, a corrupt persisted entry), not to be logged out as a side
	// effect. Previously this only reset `kv`'s own single localStorage
	// entry, the in-memory TanStack Query cache, and the Cache Storage API
	// (`caches`) -- the persister's actual IndexedDB entries
	// (`clearPersistStorage`, keyed separately from the in-memory cache)
	// survived and were simply restored on the very next boot, as did the
	// Dexie offline-download DB (favorited issues/articles/styles), the
	// observability DB, and every other localStorage key. `AUTH_STORAGE_KEYS`
	// is the one thing deliberately spared throughout.
	// Shared with Settings → Offline & storage (`@features/clear-app-data`),
	// which documents what it clears and why auth is spared.
	const clearAppData = useClearAppData();
	const onClickClearCache = () => {
		void clearAppData();
	};

	const onClickExportDebugInfo = () => {
		exportDebugZip().catch((error: unknown) => {
			logger.error('Failed to export debug info', error);
		});
	};

	// eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
	const v = <span onClick={handleClick}> {version}</span>;

	return (
		<Dialog
			open={show}
			onOpenChange={(open) => {
				if (!open) {
					setShow(false);
				}
			}}
		>
			<DialogContent
				className={cn(
					dialogCardClassName,
					styles['container'],
				)}
				backdropClassName={dialogCardBackdropClassName}
			>
				<div className={styles['about-dialog']}>
					<h3>{title}</h3>
					<h4>
						<T k="about.version" values={{ version: v }} />
					</h4>
					<h4 title={buildDate}>
						{t('about.build', { number: buildNumber })}
					</h4>
					{/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
					<h4
						onClick={onClickClearCache}
						className={styles['clear-cache']}
					>
						{t('about.clearCache')}
					</h4>
					{/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
					<h4
						onClick={onClickExportDebugInfo}
						className={styles['clear-cache']}
					>
						{t('about.exportDebug')}
					</h4>
					{about ? (
						<div dangerouslySetInnerHTML={{ __html: sanitizeHtml(about) }} />
					) : null}
				</div>
			</DialogContent>
		</Dialog>
	);
};

interface AboutProps {
	show: boolean;
	setShow: (value: boolean) => void;
}

export function AboutButton() {
	const t = useT();
	const { setShowDialog } = useDialog();
	const className = [styles['about'], 'tooltip down cursor-pointer'].join(
		' ',
	);

	const onClick = () => {
		setShowDialog(true);
	};

	return (
		<button
			type="button"
			className={className}
			data-text={t('common.about')}
			aria-label={t('common.about')}
			onClick={onClick}
		>
			<Info color="var(--topbar-buttons-stroke)" />
		</button>
	);
}
